/**
 * Guards against booking the same seat twice.
 *
 * `/offers` can return more than one offer with the same product for the same
 * train. Search groups offers by product and books every offer in the group,
 * so two such offers ended up in one POST /bookings with the same passengers —
 * the customer was charged twice for one journey.
 *
 * Several offers in one group are legitimate only when they cover different
 * legs (connecting trip) or different passengers (INDIVIDUAL offers split per
 * passenger). These helpers keep exactly that and nothing more.
 */

type CoverageOffer = {
    offerId?: string;
    passengerRefs?: string[];
    tripCoverage?: { coveredLegIds?: string[] };
    admissionOfferParts?: { passengerRefs?: string[] }[];
};

/** Passengers an offer is priced for: its own refs, else the union of its admission parts' refs. */
export function getOfferPassengerRefs(offer: CoverageOffer | undefined): string[] {
    if (!offer) return [];
    if (offer.passengerRefs && offer.passengerRefs.length > 0) return offer.passengerRefs;
    const refs = new Set<string>();
    (offer.admissionOfferParts || []).forEach(part => (part?.passengerRefs || []).forEach(ref => refs.add(ref)));
    return [...refs];
}

const WHOLE_LEG = "*";

/**
 * Drops every offer that sells a (leg, passenger) already sold by an earlier
 * offer in the list. Order is preserved and the first offer wins. An offer
 * without passenger refs is treated as covering every passenger on its legs.
 */
export function dropOverlappingOffers<T extends CoverageOffer>(offers: T[]): T[] {
    const soldPerLeg = new Map<string, Set<string>>();
    return offers.filter(offer => {
        const legIds = offer?.tripCoverage?.coveredLegIds || [];
        const offerRefs = getOfferPassengerRefs(offer);
        const refs = offerRefs.length > 0 ? offerRefs : [WHOLE_LEG];

        const overlaps = legIds.some(legId => {
            const sold = soldPerLeg.get(legId);
            if (!sold || sold.size === 0) return false;
            return sold.has(WHOLE_LEG) || refs.includes(WHOLE_LEG) || refs.some(ref => sold.has(ref));
        });
        if (overlaps) return false;

        legIds.forEach(legId => {
            const sold = soldPerLeg.get(legId) ?? new Set<string>();
            refs.forEach(ref => sold.add(ref));
            soldPerLeg.set(legId, sold);
        });
        return true;
    });
}

/**
 * Passengers to book on `offerId`: the offer's own refs when they are a known,
 * non-empty subset of the booking's passengers, otherwise all passengers
 * (previous behaviour, e.g. when the offer is not in `knownOffers`).
 */
export function resolveOfferPassengerRefs(
    offerId: string,
    knownOffers: CoverageOffer[] | undefined,
    allPassengerRefs: string[],
): string[] {
    const offer = knownOffers?.find(o => o?.offerId === offerId);
    const refs = getOfferPassengerRefs(offer);
    const allRefs = new Set(allPassengerRefs);
    if (refs.length > 0 && refs.every(ref => allRefs.has(ref))) return refs;
    return allPassengerRefs;
}

export type BookingOffer = { offerId: string; passengerRefs: string[] };

/**
 * Builds the `offers` of a POST /bookings request.
 *
 * Each offer is booked for the passengers it was priced for (`/offers` may split
 * one fare into several offers, e.g. one per passenger type), and an offer that
 * would re-book a (leg, passenger) already booked by an earlier offer is dropped.
 *
 * If a passenger would be left on no offer at all (e.g. an infant missing from
 * the offers' refs), it falls back to the previous behaviour — every offer for
 * every passenger, nothing dropped — so nobody is silently left off the booking.
 */
export function buildBookingOffers(
    offerIds: string[],
    searchOffers: CoverageOffer[],
    allPassengerRefs: string[],
): { offers: BookingOffer[]; droppedOfferIds: string[]; usedFallback: boolean } {
    const requested = offerIds.map(offerId => ({
        offerId,
        passengerRefs: resolveOfferPassengerRefs(offerId, searchOffers, allPassengerRefs),
        tripCoverage: searchOffers.find(o => o?.offerId === offerId)?.tripCoverage,
    }));

    const booked = new Set(requested.flatMap(o => o.passengerRefs));
    if (!allPassengerRefs.every(ref => booked.has(ref))) {
        return {
            offers: offerIds.map(offerId => ({ offerId, passengerRefs: allPassengerRefs })),
            droppedOfferIds: [],
            usedFallback: true,
        };
    }

    const kept = dropOverlappingOffers(requested);
    return {
        offers: kept.map(({ offerId, passengerRefs }) => ({ offerId, passengerRefs })),
        droppedOfferIds: requested.filter(o => !kept.includes(o)).map(o => o.offerId),
        usedFallback: false,
    };
}
